import React, { useState } from "react";
import {
  LinkIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import TabButton from "../components/TabButton";
import CertificateChainDiagram from "../components/CertificateChainDiagram";
import CertificateExtensions from "../components/CertificateExtensions";
import SEO from "../components/SEO";
import api from "../utils/api";
import { downloadFile, formatDate } from "../utils/helpers";
import {
  generateStandardFilename,
  sanitizeFilename,
  generateTimestamp,
} from "../../shared/utils/fileHelpers";
import { downloadChainBundle } from "../utils/zipHelper";
import {
  certificateName,
  certificateDisplayName,
} from "../../shared/utils/certificateNaming";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";

function ChainAnalyzer() {
  const [certificate, setCertificate] = useState("");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("paste");
  const resultsRef = useScrollToResults(result);
  // 2026-07-04 tools-wide overhaul (operator-approved), audit T3 #11 +
  // #12 : the never-implemented modal state (selectedCertificate /
  // showCertModal / showDetails) was removed as dead code, and this
  // state-driven copy feedback was added in its place, mirroring the
  // copyStatus pattern in CSRGenerator.jsx so every tool gives the
  // same transient "Copied!" confirmation instead of a silent copy.
  // State-driven copy with transient feedback via the shared
  // success-gated hook (see src/client/hooks/useCopyFeedback.js).
  // History : the previous local handleCopy was a no-op wrapper
  // ("Could add a notification here") and the per-index
  // handleDownloadCertificate was never referenced (per-certificate
  // downloads live inside CertificateChainDiagram) ; both were
  // removed and replaced with the CSRGenerator handleCopy idiom in
  // the 2026-07-04 tools-wide overhaul, audit T3 #11 + #12, which the
  // hook then absorbed.
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeTab === "paste" && !certificate.trim()) {
      setError("Please provide an SSL Certificate to analyze.");
      return;
    }

    if (activeTab === "url") {
      if (!url.trim()) {
        setError("Please provide a URL to analyze.");
        return;
      }
      // Basic domain validation
      const domainRegex =
        /^([a-zA-Z0-9][a-zA-Z0-9-]*\.)*[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}$/;
      const urlWithoutProtocol = url.replace(/^https?:\/\//, "");
      if (!domainRegex.test(urlWithoutProtocol)) {
        setError("Please enter a valid domain name.");
        return;
      }
    }

    setLoading(true);
    setError(null);
    setResult(null);
    // Stale copy feedback goes with the result it belonged to
    // (positional keys would otherwise light up the NEW result's row
    // inside the 2-second window).
    resetCopyStatus();

    try {
      let response;
      if (activeTab === "paste") {
        response = await api.analyzeChain(certificate);
      } else {
        // Add https:// if not present
        const fullUrl =
          url.startsWith("http://") || url.startsWith("https://")
            ? url
            : `https://${url}`;
        response = await api.analyzeChainByUrl(fullUrl);
      }
      setResult(response.data);
    } catch (err) {
      setError(
        err.message || "The SSL Certificate chain could not be analyzed."
      );
    } finally {
      setLoading(false);
    }
  };

  // ONE BUILDER FOR THE CHAIN FILE, used by all three buttons
  // (2026-08-19 review round 17). Extras are excluded because the page
  // has just told the customer to remove them, and handing the file
  // back with them concatenated in undoes the advice.
  //
  // It was applied to the download only. Copy PEM Chain sat directly
  // above it with no filter, and the ZIP built its own copy - so three
  // adjacent buttons gave three answers, two of them writing a file
  // called <domain>-chain.pem whose contents depended on which was
  // pressed. A customer who reads "you can remove it" and then presses
  // Copy, because pasting is what they were about to do, got the extra
  // straight back. The filter belongs to the artifact, not to a button.
  const chainPem = (chain) =>
    chain
      .filter((cert) => !cert.notPartOfChain)
      .map((cert) => cert.certificate)
      .join("\n");

  const handleExportJSON = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      certificate: result.certificate,
      chain: result.chain,
      validation: result.validation,
    };
    // Bare name : a filename cannot carry the "+ N More SANs" suffix.
    const domain = certificateName(result.certificate) || "certificate";
    const filename = generateStandardFilename(domain, "analysis", "json");
    downloadFile(
      JSON.stringify(exportData, null, 2),
      filename,
      "application/json"
    );
  };

  const handleExportPEM = () => {
    const pemContent = chainPem(result.chain);
    // Bare name : a filename cannot carry the "+ N More SANs" suffix.
    const domain = certificateName(result.certificate) || "certificate";
    const filename = generateStandardFilename(domain, "chain", "pem");
    downloadFile(pemContent, filename);
  };

  const handleDownloadAllZip = async () => {
    try {
      await downloadChainBundle(
        result,
        generateStandardFilename,
        sanitizeFilename,
        generateTimestamp
      );
    } catch {
      setError("The ZIP archive could not be created.");
    }
  };

  const handleReset = () => {
    setCertificate("");
    setUrl("");
    setResult(null);
    setError(null);
    resetCopyStatus();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/*
        2026-07-04 tools-wide overhaul (operator-approved) : SEO metas
        recased per the style guide ("SSL Certificate", never "SSL"
        alone when referring to SSL Certificates) and the canonical
        URL added - the shared SEO component only emits the canonical
        link when the prop is provided.
      */}
      <SEO
        title="SSL Certificate Chain Analyzer - Validate SSL Certificate Chain"
        description="Analyze and validate SSL Certificate chains online. Check SSL Certificate chain order, validate Intermediate SSL Certificates, and verify Root CA trust. Free SSL Certificate chain validation tool."
        keywords="SSL Certificate chain analyzer, SSL Certificate chain validator, SSL Certificate chain, validate SSL Certificate chain, Intermediate SSL Certificate checker, SSL Certificate chain order, SSL Certificate chain verification, SSL Certificate trust chain"
        canonicalUrl="https://tools.trustico.com/chain"
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          SSL Certificate Chain Analyzer
        </h1>
        <p className="text-body">
          Check that an SSL Certificate and its intermediate SSL Certificates
          are installed correctly and form a complete chain. Paste the SSL
          Certificates from your server, or fetch them directly from a live
          website, and the analyzer reports what was sent, what signs what, and
          anything that is missing or expired. An incomplete chain is one of the
          most common installation problems, so it is worth checking after every
          installation. The full details of every SSL Certificate found are
          shown, including names, validity dates and extensions.
        </p>
      </div>

      <SecurityNotice />

      <form onSubmit={handleSubmit} className="card p-6">
        {/* 2026-07-04 operator consistency directive : the paste-vs-URL
            TabButton pair previously sat with no section heading and no
            explanatory text, which the operator flagged directly ("no
            explanatory text above the two buttons that people have to
            choose from and also a missing title above where that would
            be too"). Every customer choice carries a standard section
            heading (text-lg font-semibold, the CSRGenerator.jsx
            reference size) plus one explanatory sentence, matching the
            "Certificate Request Information" treatment on the
            reference page. */}
        <h2 className="text-lg font-semibold text-heading mb-2">
          Choose an Input Method
        </h2>
        <p className="text-body mb-4">
          Select whether to paste an SSL Certificate in PEM format or obtain the
          SSL Certificate chain directly from a live website.
        </p>
        {/* Tab Navigation */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 max-w-2xl mx-auto">
          <TabButton
            active={activeTab === "paste"}
            onClick={() => setActiveTab("paste")}
          >
            Paste SSL Certificate
          </TabButton>
          <TabButton
            active={activeTab === "url"}
            onClick={() => setActiveTab("url")}
          >
            Obtain from URL
          </TabButton>
        </div>

        {activeTab === "paste" && (
          <div>
            <label htmlFor="certificate" className="label">
              SSL Certificate
            </label>
            <p className="text-body mb-3">
              Paste one or more SSL Certificates in PEM format below. You can
              include the entire SSL Certificate chain (end-entity,
              intermediate, and root SSL Certificates) to analyze the complete
              trust path. Each SSL Certificate must be in PEM format.
            </p>
            <textarea
              id="certificate"
              value={certificate}
              onChange={(e) => setCertificate(e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
              className="textarea certificate-textarea"
              rows={12}
            />
          </div>
        )}

        {activeTab === "url" && (
          <div>
            <label htmlFor="url" className="label">
              Website URL
            </label>
            <p className="text-body mb-3">
              Enter a domain name to fetch and analyze the SSL Certificate chain
              directly from the server. The tool will connect to the server on
              port 443 and retrieve the complete SSL Certificate chain for
              analysis.
            </p>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-muted">https://</span>
              </div>
              <input
                id="url"
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="example.com"
                className="input pl-20"
              />
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-center">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
            <ActionButton
              type="submit"
              variant="primary"
              disabled={
                loading ||
                (activeTab === "paste" ? !certificate.trim() : !url.trim())
              }
              loading={loading}
              loadingText="Analyzing..."
              icon={LinkIcon}
            >
              Analyze Chain
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleReset}
              variant="outline"
              icon={XMarkIcon}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data" ; normalized in the
                  same sweep on all sibling pages). */}
              Reset
            </ActionButton>
          </div>
        </div>
      </form>

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <div ref={resultsRef} className="mt-8 space-y-6 animate-fade-in">
          {/* Certificate Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Main Certificate Info */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                End Entity SSL Certificate
              </h2>
              {/* 2026-07-04 operator consistency directive : every
                  section heading carries an explanatory paragraph (the
                  CSRGenerator.jsx reference idiom) ; the result-section
                  headings on this page previously had none. */}
              <p className="text-body mb-4">
                The end entity SSL Certificate is the one issued for your
                domain, and the only one a visitor&apos;s browser is actually
                shown.
              </p>

              <div className="space-y-3">
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  {/* ISSUED TO, NOT DOMAIN (operator ruling,
                      2026-08-21 : "it should be using the official
                      naming"). Not every SSL Certificate identifies a
                      domain - an S/MIME certificate identifies a person
                      by e-mail address - so the label was wrong for
                      those, not merely the empty value beneath it.
                      Issued To is true of every kind. */}
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Issued To
                  </span>
                  <p className="text-heading font-mono font-medium mt-1">
                    {certificateDisplayName(result.certificate) || "N/A"}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Organization
                  </span>
                  <p className="text-heading font-mono mt-1">
                    {result.certificate.subject.organizationName || "N/A"}
                  </p>
                  {result.certificate.subject.organizationalUnitName && (
                    <p className="text-body font-mono">
                      {result.certificate.subject.organizationalUnitName}
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Location
                  </span>
                  <p className="text-heading font-mono mt-1">
                    {[
                      result.certificate.subject.localityName,
                      result.certificate.subject.stateOrProvinceName,
                      result.certificate.subject.countryName,
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Validity Period
                  </span>
                  <p className="text-heading font-mono mt-1">
                    {formatDate(result.certificate.notBefore)} -{" "}
                    {formatDate(result.certificate.notAfter)}
                  </p>
                  <p
                    className={`font-mono font-medium mt-1 ${result.certificate.isExpired ? "text-red-600 dark:text-red-300" : "text-green-700 dark:text-green-400"}`}
                  >
                    {result.certificate.isExpired
                      ? "EXPIRED"
                      : `VALID (${result.certificate.daysUntilExpiry} Days Remaining)`}
                  </p>
                </div>
              </div>
            </div>

            {/* Technical Details */}
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-heading mb-2">
                Technical Details
              </h2>
              {/* Explanatory paragraph per the 2026-07-04 operator
                  consistency directive (see the End Entity card above). */}
              <p className="text-body mb-4">
                These are the cryptographic properties of the SSL Certificate
                and the method the Certificate Authority (CA) used to sign it.
              </p>

              <div className="space-y-3">
                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Issuer
                  </span>
                  <p className="text-heading font-mono mt-1">
                    {result.certificate.issuer.commonName ||
                      result.certificate.issuer.organizationName ||
                      "N/A"}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Serial Number
                  </span>
                  <p className="text-heading font-mono break-all mt-1">
                    {result.certificate.serialNumber}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Key Algorithm
                  </span>
                  <p className="text-heading font-mono mt-1">
                    {result.certificate.publicKey.algorithm}
                    {result.certificate.publicKey.bits
                      ? ` (${result.certificate.publicKey.bits} bits)`
                      : result.certificate.publicKey.curve
                        ? ` (${result.certificate.publicKey.curve})`
                        : ""}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    Signature Algorithm
                  </span>
                  <p className="text-heading font-mono mt-1">
                    {result.certificate.signatureAlgorithm}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                  <span className="text-xs font-medium text-muted uppercase tracking-wider">
                    SHA-256 Fingerprint
                  </span>
                  <p className="text-heading font-mono break-all mt-1">
                    {result.certificate.fingerprints.sha256}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Subject Alternative Names */}
          {result.certificate.extensions?.subjectAltName &&
            result.certificate.extensions.subjectAltName.length > 0 && (
              <div className="card p-6">
                <h2 className="text-lg font-semibold text-heading mb-2">
                  Subject Alternative Names
                </h2>
                {/* Explanatory paragraph per the 2026-07-04 operator
                    consistency directive (see the End Entity card above). */}
                <p className="text-body mb-4">
                  Subject Alternative Names list every domain name this SSL
                  Certificate covers. A browser matches the address it is
                  visiting against this list and nothing else, so a name that is
                  absent here is refused even though the SSL Certificate is
                  valid. A wildcard such as *.example.com covers one level of
                  sub-domain only, which is why example.com is usually listed
                  separately.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {result.certificate.extensions.subjectAltName.map(
                    (san, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg"
                      >
                        <span className="text-xs font-medium text-muted uppercase tracking-wider">
                          {san.type}
                        </span>
                        <p className="text-heading font-mono mt-1 break-all">
                          {san.value}
                        </p>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

          {/* Certificate Extensions */}
          {result.certificate.extensions && (
            <CertificateExtensions extensions={result.certificate.extensions} />
          )}

          {/* Chain Visualization */}
          {result.chain && result.chain.length > 0 && (
            <div className="card p-6">
              {/* 2026-07-04 operator consistency directive : the
                  flex-justify-between wrapper around this heading held
                  nothing but the heading (a leftover from a removed
                  header control), so it was dropped in the same pass
                  that added the explanatory paragraph every section
                  heading must carry. */}
              <h2 className="text-lg font-semibold text-heading mb-2">
                SSL Certificate Chain Visualization
              </h2>
              <p className="text-body mb-4">
                This diagram shows every SSL Certificate in the chain in order,
                starting with the one issued for your domain and ending at a
                root. Each SSL Certificate is signed by the one shown below it.
                Anything marked as not installed did not come from what you
                provided - this tool found it, and it is shown so you can see
                the full path rather than only the part you have.
              </p>
              {/*
                2026-07-04 tools-wide overhaul (operator-approved),
                audit T3 #11 : the IIFE try/catch that wrapped this
                render was dead code (creating a React element never
                throws synchronously ; render failures are caught by
                the app-level ErrorBoundary, not a local try/catch).
                The inert onCertificateClick stays because
                CertificateChainDiagram invokes the prop unguarded in
                its header onClick (see the onCertificateClick call
                site in CertificateChainDiagram.jsx) ; omitting it
                would crash on click. The details modal it was meant
                to open was never implemented, so the callback is
                deliberately a no-op.
              */}
              <CertificateChainDiagram
                fromServer={Boolean(result.source)}
                chain={result.chain}
                onCertificateClick={() => {}}
              />
            </div>
          )}

          {/* Validation Results */}
          <div className="card p-6 bg-linear-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
            <h2 className="text-lg font-semibold text-heading mb-2">
              What Was Found
            </h2>
            {/*
              THREE FACTS, NO FORECAST (operator ruling 2026-08-19).
              This heading was "Validation Results" over a green VALID
              or a red INVALID, and the paragraph promised the chain was
              "genuine and complete". None of that is ours to say :
              whether a chain works depends on what the CLIENT already
              holds, and a mobile device may carry a root a browser does
              not, or a different cross-signed form of the same one. The
              page reports what arrived, what this tool had to find for
              itself, and only faults it can prove.
            */}
            <p className="text-body mb-4">
              Each SSL Certificate is checked against the one below it to
              confirm it really was signed by it. Whether a browser accepts the
              chain also depends on which SSL Certificates that device already
              holds, which differs between browsers and mobile devices and
              cannot be seen from here. This page reports what is installed,
              what is not, and anything demonstrably wrong.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/*
                ONE TREATMENT FOR EVERY LABELLED BOX ON THIS PAGE
                (operator decision, 2026-08-24, asked and answered
                directly : "match them completely"). These three were a
                coloured strip - tinted fill, 2px border, a 32px icon, a
                12px non-uppercase label and an 18px bold mono value -
                while every other box on the same page used the small
                uppercase label over a base-size mono value on plain
                grey. He was told in the question that this removes the
                red / green on the Problems tile and chose it anyway.
                The box, the label and the value below are copied from
                the Technical Details rows further down this file
                (search for `uppercase tracking-wider`) ; keep all three
                in step if any of them moves.
              */}
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  {result.source ? "Installed" : "Supplied"}
                </span>
                <p className="text-heading font-mono mt-1">
                  {result.validation.installed} SSL{" "}
                  {result.validation.installed === 1
                    ? "Certificate"
                    : "Certificates"}
                </p>
              </div>

              {/*
                NOT A WARNING. A certificate this tool found for itself
                is missing from their server, and that is all it means.
                Most Certificate Authorities, this one included, tell
                customers not to install the root at all, and an
                intermediate may equally be found by the client - so
                neither absence is a fault. Until 2026-08-24 that was
                said by giving both tiles the same neutral blue rather
                than a warning colour ; the three tiles now carry no
                colour at all (operator decision, see the comment on the
                first tile), so the ruling survives in the WORDING -
                "Not Installed" is a statement, never "Missing".
              */}
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  {result.source ? "Not Installed" : "Not Supplied"}
                </span>
                <p className="text-heading font-mono mt-1">
                  {result.validation.notInstalled} SSL{" "}
                  {result.validation.notInstalled === 1
                    ? "Certificate"
                    : "Certificates"}
                </p>
              </div>

              {/*
                WHAT COUNTS AS A PROBLEM, unchanged : only what is
                provably wrong in the bytes - a signature that did not
                verify, a certificate expired or not yet valid, one that
                could not be read, one unrelated to the rest. This tile
                was the only red light on the page until 2026-08-24 and
                now reads like the other two ; the distinction a
                customer needs is carried by the VALUE, which is either
                a count or the word NONE, so it survives without the
                colour and without the icon.
              */}
              <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg min-h-[80px]">
                <span className="text-xs font-medium text-muted uppercase tracking-wider">
                  Problems Found
                </span>
                <p className="text-heading font-mono mt-1">
                  {result.validation.problems?.length
                    ? result.validation.problems.length
                    : "NONE"}
                </p>
              </div>
            </div>

            {/*
              EVERY FINDING, IN THE TWO CATEGORIES THAT MATTER. A
              problem is provably wrong in the bytes and takes the red
              treatment. A note is true, worth saying, and not a fault -
              a certificate the server did not send, a spare one in the
              file, a signature this runtime cannot check - and takes
              amber, matching the badge the diagram uses for the same
              class of thing. Rendering them from one combined list put
              a red alarm beside notes that broke nothing.
            */}
            {(result.validation.problems?.length > 0 ||
              result.validation.notes?.length > 0) && (
              <div className="mt-6">
                <ul className="space-y-2">
                  {[
                    ...(result.validation.problems || []).map((text) => ({
                      text,
                      breaking: true,
                    })),
                    ...(result.validation.notes || []).map((text) => ({
                      text,
                      breaking: false,
                    })),
                  ].map(({ text, breaking }, index) => (
                    <li key={index} className="flex items-start text-body">
                      <ExclamationTriangleIcon
                        className={`w-5 h-5 mr-2 shrink-0 mt-0.5 ${
                          breaking
                            ? "text-red-600 dark:text-red-400"
                            : "text-amber-600 dark:text-amber-400"
                        }`}
                      />
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Export Options */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Export Options
            </h2>
            <p className="text-body mb-4">
              Copy the complete SSL Certificate chain to your clipboard,
              download it in various formats for use in different applications
              and servers, or download everything in a single ZIP archive.
            </p>
            <div className="flex justify-center">
              {/*
                2026-07-04 tools-wide overhaul (operator-approved),
                audit T3 #12 : the PEM chain is the artifact customers
                paste into server configs, so it gets a real
                state-driven Copy button with the transient "Copied!"
                confirmation (the CSRGenerator.jsx copyStatus pattern)
                instead of the old silent, unused copy handler.
              */}
              {/* Standard two-column action row (the CSRGenerator
                  results-card idiom). NOT four-across : ActionButton
                  carries min-w-[280px] at lg, and four of those plus
                  three gaps need 1168px - more than any container on
                  this page can offer - so a lg:grid-cols-4 row forced
                  the equal-size buttons to overlap each other
                  (operator-reported on /chain, 2026-08-07). Two
                  columns keep every button the same size with room to
                  spare at every breakpoint. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                <ActionButton
                  onClick={() => handleCopy(chainPem(result.chain), "chain")}
                  variant="outline"
                  icon={ClipboardDocumentIcon}
                >
                  {copyStatus.chain ? "Copied!" : "Copy PEM Chain"}
                </ActionButton>
                <ActionButton
                  onClick={handleExportPEM}
                  variant="outline"
                  icon={ArrowDownTrayIcon}
                >
                  Export as PEM Chain
                </ActionButton>
                <ActionButton
                  onClick={handleExportJSON}
                  variant="outline"
                  icon={ArrowDownTrayIcon}
                >
                  Export as JSON
                </ActionButton>
                <ActionButton
                  onClick={handleDownloadAllZip}
                  variant="primary"
                  icon={ArchiveBoxIcon}
                >
                  Download ZIP Archive
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChainAnalyzer;
